import { pictureSchema } from '@omecare/utils';
import {
  UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { EditAdminProfileFormValues } from '@/components/forms/edit-admin-profile/schema';
import http from '@/utils/http.service';

const editAdminProfileResponseSchema = z.object({
  id: z.string().uuid(),
  firstname: z.string(),
  lastname: z.string(),
  email: z.string(),
  picture: pictureSchema,
  createdAt: z.coerce.date(),
});

export type EditAdminProfileResponse = z.infer<
  typeof editAdminProfileResponseSchema
>;
export type EditAdminProfilePayload = {
  deleteAvatar: boolean;
} & Omit<EditAdminProfileFormValues, 'picture'>;

const editAdminProfile = (payload: EditAdminProfilePayload) => {
  return http.users
    .updateProfile(payload)
    .then<EditAdminProfileResponse>(({ response, transform }) => {
      const result = transform.handle({
        response,
        attributes: {
          firstname: true,
          lastname: true,
          picture: true,
          email: true,
          createdAt: true,
        },
        schema: editAdminProfileResponseSchema,
      });

      return result;
    });
};

const useEditAdminProfileMutation = (): UseMutationResult<
  EditAdminProfileResponse,
  Error,
  EditAdminProfilePayload
> => {
  const queryClient = useQueryClient();

  const { t } = useTranslation();
  return useMutation({
    mutationFn: editAdminProfile,
    onSuccess() {
      toast.success(t('http.edit-profile.success'));
      queryClient.invalidateQueries({
        queryKey: ['me'],
      });
    },
  });
};

export default useEditAdminProfileMutation;
